import Handlebars from 'handlebars';
import { Button, Card, Grid, Group, Text, TextInput, Textarea } from '@mantine/core';
import { IconAlertTriangle, IconBell, IconCode, IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import getMergeTags from '../../constants/getMergeTags';
import useFetch from '../../helpers/useFetch';
import AssetPickerModal from '../Assets/AssetPickerModal';
import MergeTagsModal from '../Modals/MergeTagsModal';
import SendTestMessageModal from '../Modals/SendTestMessageModal';
import TestMethodSelectionModal from '../Modals/TestMethodSelectionModal';
import PushNotificationPreview from './PushNotificationPreview';

const ToastWrapper = ({ children, pushNotification, platform, extraFooter, actionButtons }) => {
  return (
    <Grid>
      <Grid.Col span={{ base: 12, sm: platform === 'web' ? 7 : 8 }}>
        {children}
        {extraFooter}
        {actionButtons && (
          <Group justify="flex-end" gap="xs" pt="md">
            {actionButtons}
          </Group>
        )}
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: platform === 'web' ? 5 : 4 }}>
        <PushNotificationPreview pushNotification={pushNotification} platform={platform} />
      </Grid.Col>
    </Grid>
  );
};

const PushNotificationContent = ({
  pushNotification,
  setPushNotification,
  platform,
  customFields,
  coupons,
  surveys = [],
  actionButtons,
  subjectHandlebarsError,
  bodyHandlebarsError,
}) => {
  const [testMethodSelectionModalOpen, setTestMethodSelectionModalOpen] = useState(false);
  const [sendTestMessageModalOpen, setSendTestMessageModalOpen] = useState();
  const [sending, setSending] = useState(false);
  const { domain } = useSelector((state) => state.domain);
  const { user } = useSelector((state) => state.auth);
  const fetch = useFetch();

  const [userError, setUserError] = useState();
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [dynamicFieldsOpen, setDynamicFieldsOpen] = useState(false);
  const [previewProduct, setPreviewProduct] = useState();

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/products?from=0&size=1`)
      .then(({ products }) => setPreviewProduct(products[0]))
      .catch(() => {});
  }, [domain.accountId, domain.id, fetch]);

  const content = pushNotification.content[platform];

  const handleChange = (name, value) => {
    setPushNotification({
      ...pushNotification,
      content: { ...pushNotification.content, [platform]: { ...pushNotification.content[platform], [name]: value } },
    });
  };

  const resolveHandlebars = (template) => {
    if (!template) return template;
    try {
      return Handlebars.compile(template)({ product: previewProduct });
    } catch {
      return template;
    }
  };

  const sendTestPushNotification = (impersonateProfileQuery, sendTo, triggerEventAction, cb) => {
    setSending(true);

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/pushNotifications/testPushNotification`, {
      method: 'POST',
      body: JSON.stringify({
        pushNotification,
        impersonateProfileQuery,
        sendTo,
        triggerEventAction,
        platform,
      }),
    })
      .then(() => {
        cb();
      })
      .catch(setUserError)
      .finally(() => setSending(false));
  };

  const sendTestNotification = (sendTo, impersonateProfileQuery, triggerEventAction, cb) => {
    setUserError(undefined);
    sendTestPushNotification(impersonateProfileQuery, sendTo, triggerEventAction, cb);
  };

  const sendBrowserPreview = () => {
    setUserError(undefined);
    if (!('Notification' in window)) {
      setUserError(new Error('This browser does not support system notifications!'));
      return;
    }

    const showNotification = () => {
      const notificationOptions = {
        body: resolveHandlebars(content.body),
        icon: resolveHandlebars(content.iconUrl),
        image: resolveHandlebars(content.imageUrl),
      };

      const notification = new Notification(resolveHandlebars(content.subject), notificationOptions);

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
    };

    if (Notification.permission === 'granted') {
      showNotification();
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission((permission) => {
        if (permission === 'granted') {
          showNotification();
        } else {
          setUserError(
            new Error(
              'You denied notification permissions for this site in this browser. Please allow them to preview notifications here.'
            )
          );
        }
      });
    } else {
      setUserError(
        new Error(
          'Notifications are blocked for this site in this browser. Please allow them in your browser settings to preview notifications here.'
        )
      );
    }
  };

  return (
    <ToastWrapper
      pushNotification={pushNotification}
      platform={platform}
      actionButtons={actionButtons}
      extraFooter={
        <Group gap="xs" align="center" wrap="nowrap" pt="md">
          <IconInfoCircle size={16} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
          <Text c="dimmed" size="sm">
            Messages may look different on different browsers and operating systems. Send yourself a test message for a
            more accurate representation.
          </Text>
        </Group>
      }
    >
      <Card withBorder>
        <Card.Section inheritPadding py="sm">
          <TestMethodSelectionModal
            show={testMethodSelectionModalOpen}
            close={() => setTestMethodSelectionModalOpen(false)}
            onSelectBrowserPreview={sendBrowserPreview}
            onSelectRealUser={() => setSendTestMessageModalOpen(true)}
          />

          <SendTestMessageModal
            send={sendTestNotification}
            close={() => setSendTestMessageModalOpen(false)}
            error={userError}
            sending={sending}
            show={sendTestMessageModalOpen}
            sendToPlaceholder="Type email, phone number (with +countryCode), or profile id"
            sendToHelperText="The test notification will be sent to all devices associated with that profile and allowed notifications for your website."
            sendTo={user.email}
          />

          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text size="sm" fw={500}>
                Subject
                <Text span c="red">
                  *
                </Text>
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <Group gap="xs" align="flex-start" wrap="nowrap">
                <TextInput
                  name="subject"
                  type="text"
                  value={content.subject}
                  placeholder="e.g. Check This Out!"
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  style={{ flex: 1 }}
                  error={subjectHandlebarsError}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTestMethodSelectionModalOpen(true)}
                  disabled={Boolean(subjectHandlebarsError || bodyHandlebarsError)}
                >
                  <IconBell size={13} style={{ marginRight: 4 }} /> Test
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDynamicFieldsOpen(!dynamicFieldsOpen)}>
                  <IconCode size={13} style={{ marginRight: 4 }} /> Merge Tags
                </Button>
              </Group>
              <Text size="sm" c="dimmed">
                This is the subject/title of your push notification.
              </Text>
              <MergeTagsModal
                show={dynamicFieldsOpen}
                close={() => setDynamicFieldsOpen(false)}
                mergeTags={getMergeTags(customFields, coupons, surveys, {
                  includeProduct: pushNotification.type === 'recommender',
                })}
              />
            </Grid.Col>
          </Grid>
          {platform === 'web' && (
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 2 }}>
                <Text size="sm" fw={500}>
                  Icon URL
                </Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 10 }}>
                <TextInput
                  name="iconUrl"
                  type="text"
                  value={content.iconUrl || ''}
                  placeholder="e.g. https://google.com/panda.jpg"
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  rightSection={
                    <Button variant="default" size="xs" onClick={() => setIconPickerOpen(true)}>
                      Browse
                    </Button>
                  }
                  rightSectionWidth={80}
                />
                <AssetPickerModal
                  show={iconPickerOpen}
                  close={() => setIconPickerOpen(false)}
                  confirm={(asset, _, cb) => {
                    handleChange('iconUrl', asset.url);
                    cb();
                  }}
                />
                <Text size="sm" c="dimmed">
                  This shows an image <strong>next to the main text</strong>.
                </Text>
              </Grid.Col>
            </Grid>
          )}

          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text size="sm" fw={500}>
                Image URL
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <TextInput
                name="imageUrl"
                type="text"
                value={content.imageUrl || ''}
                placeholder="e.g. https://google.com/panda.jpg"
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                rightSection={
                  <Button variant="default" size="xs" onClick={() => setImagePickerOpen(true)}>
                    Browse
                  </Button>
                }
                rightSectionWidth={80}
              />
              <AssetPickerModal
                show={imagePickerOpen}
                close={() => setImagePickerOpen(false)}
                confirm={(asset, _, cb) => {
                  handleChange('imageUrl', asset.url);
                  cb();
                }}
              />
              <Text size="sm" c="dimmed">
                {platform === 'ios' ? (
                  <>
                    This image will be shown when you <strong>hold</strong> on the notification <br />
                  </>
                ) : (
                  <>
                    This image will be shown <strong>below the main text</strong> <br />
                  </>
                )}

                {platform === 'web' && (
                  <strong>
                    <IconAlertTriangle size={14} /> Note that this feature is not supported on all operating systems /
                    browsers
                  </strong>
                )}
              </Text>
            </Grid.Col>
          </Grid>

          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text size="sm" fw={500}>
                Body
                <Text span c="red">
                  *
                </Text>
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <Textarea
                style={{ width: '100%' }}
                rows={3}
                onChange={(e) => handleChange('body', e.target.value)}
                name="body"
                value={content.body}
                error={bodyHandlebarsError}
              />
              <Text size="sm" c="dimmed">
                Longer piece of text to display to your customer.
              </Text>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text size="sm" fw={500}>
                Button Text
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <TextInput
                name="btnText"
                type="text"
                value={content.btnText}
                placeholder="Leave blank for no button"
                onChange={(e) => handleChange(e.target.name, e.target.value)}
              />
              <Text size="sm" c="dimmed">
                {platform === 'ios' && (
                  <>
                    {' '}
                    This button will be shown when you <strong>hold</strong> on the notification <br />
                  </>
                )}
              </Text>
            </Grid.Col>
          </Grid>
        </Card.Section>
      </Card>
    </ToastWrapper>
  );
};

export default PushNotificationContent;
